import { useEffect } from 'react';

const ANIMATED_SELECTORS = '.fade-in-section, .slide-in-left, .slide-in-right';

export const useScrollAnimation = () => {
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    /** Attach observer to an element — if already above the fold, make it
     *  visible immediately so lazy-rendered sections don't flash white. */
    const attachElement = (el: Element) => {
      if (el.classList.contains('visible')) return;
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight + 100) {
        // Already visible or very close — reveal without waiting for scroll
        el.classList.add('visible');
      } else {
        observer.observe(el);
      }
    };

    // Handle all elements already in the DOM
    document.querySelectorAll(ANIMATED_SELECTORS).forEach(attachElement);

    // Watch for elements added dynamically via LazySection / Suspense
    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          const el = node as Element;
          if (el.matches(ANIMATED_SELECTORS)) attachElement(el);
          el.querySelectorAll(ANIMATED_SELECTORS).forEach(attachElement);
        });
      });
    });

    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, []);
};